/**
 * /billing — "Manage billing" surface. PRD-003 GTM-12, GTM-18, PR-51, US-11,
 * T-030.
 *
 * Guarded (subscriber or operator). Renders a short explainer and a single
 * "Open billing" action that calls POST /api/billing/portal, then redirects
 * the browser to the Stripe-hosted Customer Portal. Payment method, the
 * Studio<->Pro switch, and cancel-at-period-end all happen inside Stripe; the
 * change reflects back through the webhook (T-029) within seconds (TECH-12).
 * Nothing here writes subscriber state.
 *
 * Integration note for T-058 (PortalLayout): backlog T-030 specifies the
 * entry point as a "Manage billing" link inside /portal/settings. The portal
 * shell does not exist yet (T-058). This standalone guarded /billing route is
 * the working surface now; when T-058 ships, the settings screen should link
 * here (or inline the `openBillingPortal` call). Surfaced in the impl status
 * report as an open coordination item.
 *
 * Voice: Eric's client-communication register — plain, direct, an open hand
 * about the downgrade timing so there is no billing surprise (GTM-18).
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { requireSubscriberOrOperator } from '@/lib/route-guards';
import { getCurrentSession } from '@/lib/auth';

export const Route = createFileRoute('/billing')({
  beforeLoad: async ({ location }) => {
    await requireSubscriberOrOperator({ redirectFrom: location.pathname });
  },
  component: BillingPage,
});

function BillingPage() {
  const [state, setState] = useState<'idle' | 'opening' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');

  async function openBillingPortal() {
    setState('opening');
    setMessage('');
    try {
      const session = await getCurrentSession();
      if (!session) {
        setState('error');
        setMessage('Your session expired. Sign in again to manage billing.');
        return;
      }
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      const json: unknown = await res.json();
      if (
        res.status === 200 &&
        typeof json === 'object' &&
        json !== null &&
        'url' in json &&
        typeof (json as { url: unknown }).url === 'string'
      ) {
        window.location.assign((json as { url: string }).url);
        return;
      }
      if (res.status === 404) {
        setState('error');
        setMessage(
          'No active subscription is linked to this account yet. If you just subscribed, give the confirmation a minute and refresh.',
        );
        return;
      }
      setState('error');
      setMessage(
        'Could not open the billing portal. Try again, or reach me through the contact form.',
      );
    } catch {
      setState('error');
      setMessage(
        'Could not reach the billing service. Please try again in a moment.',
      );
    }
  }

  return (
    <main className="min-h-screen bg-background px-6 py-16">
      <div className="mx-auto max-w-lg space-y-6">
        <h1 className="font-display text-3xl text-foreground">Billing</h1>
        <div className="space-y-4 text-base leading-relaxed text-foreground">
          <p>
            Your payment method, plan changes, and cancellation are all handled
            in Stripe&apos;s secure billing portal. The button below opens it.
          </p>
          <p>
            Moving from Studio to Pro takes effect right away, so your next pull
            runs at the new tier. Moving from Pro down to Studio takes effect at
            the end of your current billing period, so nothing in flight gets
            cut short. Cancelling works the same way: your active work finishes
            its cycle.
          </p>
        </div>
        <button
          type="button"
          onClick={openBillingPortal}
          disabled={state === 'opening'}
          className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {state === 'opening' ? 'Opening...' : 'Open billing portal'}
        </button>
        {state === 'error' ? (
          <p className="text-sm text-destructive">{message}</p>
        ) : null}
        <p className="text-sm text-muted-foreground">
          Questions about billing that the portal does not answer? Use the{' '}
          <a
            href="https://yesplease.studio/contact"
            className="text-primary underline underline-offset-4"
          >
            contact form
          </a>{' '}
          and I will help directly.
        </p>
      </div>
    </main>
  );
}
